import { ModelSetTransformer, ModelTransformer } from '../common/common.model';
import { Data } from '../form/form.model';
import { ExtUser } from '../user/user.model';

export enum Mode {
    MEMBER,
    MANAGER,
}

export interface ReviewDateRange {
    start: Date | string;
    end: Date | string;
    displayName: string;
}

export interface Text {
    key: string;
    default: string;
}

export interface Metadata {
    reviewDateRanges: ReviewDateRange[];
    texts: Text[];
}

export class MetadataModelTransformer
    implements ModelTransformer<Metadata, Metadata>
{
    transform(model: Metadata): Metadata {
        return {
            ...model,
            reviewDateRanges: model.reviewDateRanges
                .map((rdr) => ({
                    ...rdr,
                    start: !rdr.start ? null : new Date(rdr.start),
                    end: !rdr.end ? null : new Date(rdr.end),
                }))
                .sort((a, b) => b.end.getTime() - a.end.getTime()),
        };
    }
}

export interface HierarchyInfoItem extends ExtUser {
    roleId: number;
    roleName: string;
    manager: HierarchyInfoItem;
}

export interface HierarchyInfo extends HierarchyInfoItem {
    team: HierarchyInfoItem[];
}

export interface ReportArgs {
    templateId: number;
    userId: number;
    reviewDateRanges: ReviewDateRange[];
    directTeamOnly: boolean;
}

export interface ManagerReportDataItem {
    start: Date | string;
    end: Date | string;
    displayName: string;
    userId: number;
    firstName: string;
    lastName: string;
    managerUserId: number;
    managerFirstName: string;
    managerLastName: string;
}

export enum Status {
    UNKNOWN,
    DRAFT,
    PENDING,
    COMPLETE,
}

export enum FlowState {
    UNKNOWN,
    MEMBER_DRAFT,
    MEMBER_SUBMITTED,
    MEMBER_REVIEW,
    MEMBER_REVIEW_SUBMITTED,
    MANAGER_DRAFT,
    COMPLETE,
}

export interface Payload {
    threadGuid: string;
    iteration?: number;
    memberId: number;
    managerId: number;
    lookingBack: string;
    lookingForward: string;
    conversationSummary: string;
    created?: Date | string;
    createdBy?: number;
    lastModified?: Date | string;
    lastModifiedBy?: number;
    lastModifiedMember?: Date | string;
    lastModifiedManager?: Date | string;
    flowState?: FlowState;
    custodyUserId?: number;
}

export interface PayloadExtended extends Payload {
    memberInfo?: HierarchyInfoItem;
    managerInfo?: HierarchyInfoItem;
}

export interface Args {
    isDraft?: boolean;
    returnToMember?: boolean;
    userId?: number;
    team?: boolean;
}

export class PayloadModelTransformer
    implements ModelTransformer<Payload, Payload>
{
    transform(model: Payload): Payload {
        return {
            ...model,
            created: !model.created ? null : new Date(model.created),
            lastModified: !model.lastModified
                ? null
                : new Date(model.lastModified),
            lastModifiedMember: !model.lastModifiedMember
                ? null
                : new Date(model.lastModifiedMember),
            lastModifiedManager: !model.lastModifiedManager
                ? null
                : new Date(model.lastModifiedManager),
        };
    }

    reverseTransform(model: Payload): Payload {
        return {
            threadGuid: model.threadGuid,
            memberId: model.memberId,
            managerId: model.managerId,
            lookingBack: model.lookingBack,
            lookingForward: model.lookingForward,
            conversationSummary: model.conversationSummary,
        };
    }
}

export interface PayloadView extends PayloadExtended {
    dataId: number;
    quarter?: string;
    status?: Status;
}

export class PayloadViewModelTransformer
    implements ModelTransformer<PayloadView, Data<PayloadExtended>>
{
    constructor(
        private readonly _mode: Mode,
        private readonly _reviewDateRanges: ReviewDateRange[],
    ) {}

    transform(model: Data<PayloadExtended>): PayloadView {
        const payload = model.payload;
        return {
            ...payload,
            dataId: model.dataId,
            quarter: quarterFor(
                payload.created as Date,
                this._reviewDateRanges,
            ),
            lastModified: lastModifiedFor([
                payload.created as Date,
                payload.lastModified as Date,
            ]),
            status: statusFor(payload.flowState, this._mode),
        };
    }
}

export class PayloadViewModelSetTransformer extends ModelSetTransformer<
    PayloadView,
    Data<PayloadExtended>
> {
    constructor(
        private readonly _mode: Mode,
        private readonly _reviewDateRanges: ReviewDateRange[],
    ) {
        super();
    }

    protected newTransformer(): ModelTransformer<
        PayloadView,
        Data<PayloadExtended>
    > {
        return new PayloadViewModelTransformer(
            this._mode,
            this._reviewDateRanges,
        );
    }

    transform(model: Array<Data<PayloadExtended>>): PayloadView[] {
        const valid = model.filter((m) => {
            const dr = reviewDateRangeFor(
                m.payload.created as Date,
                this._reviewDateRanges,
            );
            return !!dr;
        });
        return super.transform(valid);
    }
}

export interface PayloadHierarchyMember {
    info: HierarchyInfoItem;
    payloads: PayloadView[];
}

export class TeamViewModelTransformer
    implements ModelTransformer<PayloadHierarchyMember[], PayloadView[]>
{
    constructor(private readonly _hierarchy: HierarchyInfo) {}

    transform(model: PayloadView[]): PayloadHierarchyMember[] {
        return this._hierarchy.team.map((h) => ({
            info: h,
            payloads: model
                .filter((m) => m.memberId === h.userId)
                .sort(
                    (a, b) =>
                        (b.created as Date).getTime() -
                        (a.created as Date).getTime(),
                ),
        }));
    }
}

export interface UserView extends PayloadHierarchyMember {
    name?: string;
    lastModified?: Date;
    status?: Status;
    payload?: PayloadView;
}

export class UserViewModelTransformer
    implements ModelTransformer<UserView, PayloadHierarchyMember>
{
    constructor(
        private readonly _mode: Mode,
        private readonly _reviewDateRanges: ReviewDateRange[],
    ) {}

    transform(model: PayloadHierarchyMember): UserView {
        // should have sorted by TeamViewModelTransformer
        const payload = model.payloads.find((p) =>
            isWithinCurrentDateRange(p.created as Date, this._reviewDateRanges),
        );
        return {
            ...model,
            name: `${model.info.lastName || ''}, ${model.info.firstName || ''}`,
            lastModified: !payload
                ? null
                : lastModifiedFor([
                      payload.created as Date,
                      payload.lastModified as Date,
                  ]),
            status: !payload
                ? Status.UNKNOWN
                : statusFor(payload.flowState, this._mode),
            payload,
        };
    }
}

export class UserViewModelSetTransformer extends ModelSetTransformer<
    UserView,
    PayloadHierarchyMember
> {
    constructor(
        private readonly _mode: Mode,
        private readonly _reviewDateRanges: ReviewDateRange[],
    ) {
        super();
    }

    protected newTransformer(): ModelTransformer<
        UserView,
        PayloadHierarchyMember
    > {
        return new UserViewModelTransformer(this._mode, this._reviewDateRanges);
    }
}

export function reviewDateRangeFor(
    date: Date,
    dateRanges: ReviewDateRange[],
): ReviewDateRange {
    if (!date || !dateRanges) return undefined;
    const time = date.getTime();
    return dateRanges.find((dr) => {
        const start = (dr.start as Date).getTime();
        const end = (dr.end as Date).getTime();
        return start <= time && time <= end;
    });
}

export function hasReviewWithin(
    dateRange: ReviewDateRange,
    data: Array<Data<Payload>>,
): boolean {
    if (!dateRange) return false;
    return data.some((d) => {
        const start = (dateRange.start as Date).getTime();
        const end = (dateRange.end as Date).getTime();
        const created = (d.payload.created as Date).getTime();
        return start <= created && created <= end;
    });
}

export function isWithinCurrentDateRange(
    date: Date,
    dateRanges: ReviewDateRange[],
): boolean {
    return (
        reviewDateRangeFor(new Date(), dateRanges) ===
        reviewDateRangeFor(date, dateRanges)
    );
}

export function quarterFor(date: Date, dateRanges: ReviewDateRange[]): string {
    try {
        const dateRange = reviewDateRangeFor(date, dateRanges);
        return dateRange.displayName;
    } catch (e) {}
    return undefined;
}

export function lastModifiedFor(dates: Date[]): Date {
    if (!dates) return undefined;
    return dates
        .filter((d) => !!d)
        .reduce<Date>((p: Date, c: Date) => {
            if (!p || !p.getTime()) return c;
            if (!c || !c.getTime()) return p;
            return c.getTime() > p.getTime() ? c : p;
        }, undefined);
}

export function statusFor(flowState: FlowState, mode: Mode): Status {
    let status = Status.UNKNOWN;
    if (flowState === FlowState.MEMBER_DRAFT) {
        status = mode === Mode.MEMBER ? Status.DRAFT : Status.PENDING;
    }
    if (
        flowState === FlowState.MEMBER_SUBMITTED ||
        flowState === FlowState.MEMBER_REVIEW ||
        flowState === FlowState.MEMBER_REVIEW_SUBMITTED
    ) {
        status = Status.PENDING;
    }
    if (flowState === FlowState.MANAGER_DRAFT) {
        status = mode === Mode.MANAGER ? Status.DRAFT : Status.PENDING;
    }
    if (flowState === FlowState.COMPLETE) {
        status = Status.COMPLETE;
    }
    return status;
}
